import { 
  User, 
  UserRole, 
  Expense, 
  ExpenseStatus, 
  ExpenseCategory, 
  PaymentMethod,
  ApprovalStatus
} from '../types';

// Demo users for testing
// Employee -> Manager mapping is done via managerEmail field
export const DEMO_USERS: User[] = [
  {
    id: 'user-employee-1',
    email: 'employee@demo.com',
    name: 'John Employee',
    employeeId: 'EMP001',
    department: 'Engineering',
    managerEmail: 'manager@demo.com', // This employee reports to Jane Manager
    role: UserRole.EMPLOYEE,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'user-employee-2',
    email: 'employee2@demo.com',
    name: 'Sarah Developer',
    employeeId: 'EMP002',
    department: 'Engineering',
    managerEmail: 'manager@demo.com', // Also reports to Jane Manager
    role: UserRole.EMPLOYEE,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'user-manager-1',
    email: 'manager@demo.com',
    name: 'Jane Manager',
    employeeId: 'MGR001',
    department: 'Engineering',
    managerEmail: 'admin@demo.com', // Manager reports to Finance Admin
    role: UserRole.MANAGER,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'user-admin-1',
    email: 'admin@demo.com',
    name: 'Finance Admin',
    employeeId: 'ADM001',
    department: 'Finance',
    managerEmail: undefined, // Finance Admin doesn't report to anyone
    role: UserRole.FINANCE_ADMIN,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'user-hr-admin-1',
    email: 'hradmin@demo.com',
    name: 'Lisa HR Admin',
    employeeId: 'HR001',
    department: 'Human Resources',
    managerEmail: 'superadmin@demo.com',
    role: UserRole.HR_ADMIN,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  },
  {
    id: 'user-superadmin-1',
    email: 'superadmin@demo.com',
    name: 'Super Admin',
    employeeId: 'SA001',
    department: 'Administration',
    managerEmail: undefined, // Super Admin doesn't report to anyone
    role: UserRole.SUPER_ADMIN,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z'
  }
];

// Demo expenses
export const DEMO_EXPENSES: Expense[] = [
  {
    id: 'exp-1',
    title: 'Client Meeting Lunch',
    category: ExpenseCategory.MEALS,
    amount: 85.50,
    date: '2024-12-15',
    description: 'Lunch with client to discuss Q1 project',
    paymentMethod: PaymentMethod.COMPANY_CARD,
    status: ExpenseStatus.SUBMITTED,
    submitterId: 'user-employee-1',
    receipts: [],
    approvals: [],
    createdAt: '2024-12-15T10:00:00Z',
    updatedAt: '2024-12-15T10:00:00Z'
  },
  {
    id: 'exp-2',
    title: 'Flight to NYC Conference',
    category: ExpenseCategory.TRAVEL,
    amount: 450.00,
    date: '2024-12-10',
    description: 'Round trip flight for tech conference',
    paymentMethod: PaymentMethod.PERSONAL_CARD,
    status: ExpenseStatus.MANAGER_APPROVED,
    submitterId: 'user-employee-1',
    receipts: [],
    approvals: [
      {
        id: 'apr-1',
        expenseId: 'exp-2',
        approverId: 'user-manager-1',
        status: ApprovalStatus.APPROVED,
        comments: 'Approved for conference attendance',
        level: 1,
        timestamp: '2024-12-11T09:00:00Z'
      }
    ],
    createdAt: '2024-12-10T08:00:00Z',
    updatedAt: '2024-12-11T09:00:00Z'
  },
  {
    id: 'exp-3',
    title: 'Office Supplies',
    category: ExpenseCategory.OFFICE_SUPPLIES,
    amount: 125.99,
    date: '2024-12-18',
    description: 'Notebooks, pens, and desk organizer',
    paymentMethod: PaymentMethod.PERSONAL_CARD,
    status: ExpenseStatus.DRAFT,
    submitterId: 'user-employee-1',
    receipts: [],
    approvals: [],
    createdAt: '2024-12-18T14:00:00Z',
    updatedAt: '2024-12-18T14:00:00Z'
  },
  {
    id: 'exp-4',
    title: 'Software License - JetBrains',
    category: ExpenseCategory.SOFTWARE,
    amount: 299.00,
    date: '2024-12-01',
    description: 'Annual IntelliJ IDEA license',
    paymentMethod: PaymentMethod.COMPANY_CARD,
    status: ExpenseStatus.FINANCE_APPROVED,
    submitterId: 'user-employee-1',
    receipts: [],
    approvals: [
      {
        id: 'apr-2',
        expenseId: 'exp-4',
        approverId: 'user-manager-1',
        status: ApprovalStatus.APPROVED,
        comments: 'Required for development work',
        level: 1,
        timestamp: '2024-12-02T10:00:00Z'
      },
      {
        id: 'apr-3',
        expenseId: 'exp-4',
        approverId: 'user-admin-1',
        status: ApprovalStatus.APPROVED,
        comments: 'Finance approved',
        level: 2,
        timestamp: '2024-12-03T11:00:00Z'
      }
    ],
    createdAt: '2024-12-01T09:00:00Z',
    updatedAt: '2024-12-03T11:00:00Z'
  },
  {
    id: 'exp-5',
    title: 'Hotel Stay - NYC',
    category: ExpenseCategory.ACCOMMODATION,
    amount: 350.00,
    date: '2024-12-10',
    description: '2 nights hotel for conference',
    paymentMethod: PaymentMethod.PERSONAL_CARD,
    status: ExpenseStatus.REIMBURSED,
    submitterId: 'user-employee-1',
    receipts: [],
    approvals: [],
    createdAt: '2024-12-10T08:00:00Z',
    updatedAt: '2024-12-15T16:00:00Z'
  }
];

// Password for all demo users
export const DEMO_PASSWORD = 'demo123';

// Check if demo mode is enabled
export const isDemoMode = (): boolean => {
  const envDemo = process.env.REACT_APP_DEMO_MODE === 'true';
  const urlDemo = window.location.search.includes('demo=true');
  const localDemo = localStorage.getItem('demoMode') === 'true';
  
  const result = envDemo || urlDemo || localDemo;
  
  console.log('[isDemoMode] Demo mode check:', {
    envDemo,
    urlDemo,
    localDemo,
    result,
    REACT_APP_DEMO_MODE: process.env.REACT_APP_DEMO_MODE
  });
  
  return result;
};

// Enable demo mode
export const enableDemoMode = (): void => {
  localStorage.setItem('demoMode', 'true');
};

// Disable demo mode
export const disableDemoMode = (): void => {
  localStorage.removeItem('demoMode');
};
